import {
  ProductViewed,
  ProductClicked,
  EventBeamModel
} from '@/types/tracking'
import generics from '@/utils/generics'
import { StoreModel } from '@/store'
import { Action, Thunk, action, thunk } from 'easy-peasy'
import { v4 as uuidV4 } from 'uuid';

const eventTypes = {
  ProductViewed: 'redventures.ecommerce.v1.ProductViewed',
  ProductClicked: 'redventures.ecommerce.v1.ProductClicked'
}

export interface TrackingModel {
  // State
  trackedEvents: EventBeamModel[]
  correlationId: string
  listId: string

  // Actions
  resetEvents: Action<TrackingModel>
  recordEvent: Action<TrackingModel, EventBeamModel>

  // Thunks
  fireBeam: Thunk<TrackingModel, Partial<EventBeamModel>>

  onProductClickedTracking: Thunk<
    TrackingModel,
    ProductClicked,
    unknown,
    StoreModel
  >
  onProductViewedTracking: Thunk<
    TrackingModel,
    ProductViewed,
    unknown,
    StoreModel
  >
}

const tracking: TrackingModel = {
  // State
  trackedEvents: [],
  listId: uuidV4(),
  /**
   * Cohesion already exposes a unique uuid that changes on page load. Lets use
   * that as the initial correlation id
   */
  correlationId: globalThis._Cohesion?.instanceId,

  // Actions
  recordEvent: action((state, event) => {
    state.trackedEvents.push(event)
  }),

  resetEvents: action(state => {
    state.trackedEvents = []
  }),

  // Thunks
  fireBeam: thunk(
    (
      actions,
      { eventName, eventPayload, skipLowerCase, recordEventOveerride }
    ) => {
      const payload = {
        '@type': eventTypes[eventName],
        ...generics.lowerCaseValues(eventPayload, skipLowerCase)
      }

      if (typeof globalThis.tagular === 'function') {
        globalThis.tagular('beam', eventName, payload)
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Event fired', eventName, payload)
      }

      const recordEventPayload = recordEventOveerride
        ? { ...payload, ...recordEventOveerride }
        : payload

      actions.recordEvent({ eventName, eventPayload: recordEventPayload })
    }
  ),

  onProductClickedTracking: thunk((actions, payload, { getState }) => {
    payload = { ...payload, correlationId: getState().correlationId }

    actions.fireBeam({
      eventName: 'ProductClicked',
      eventPayload: {...payload, listId: getState().listId},
      skipLowerCase: ['name', 'actionOutcome']
    })
  }),

  onProductViewedTracking: thunk(
    (actions, payload, { getStoreState, getState }) => {
      actions.fireBeam({
        eventName: 'ProductViewed',
        eventPayload: {...payload, listId: getState().listId},
        skipLowerCase: []
      })
    }
  )
}

export default tracking
